import {
  CheckCircleOutline,
  ErrorOutline,
  HourglassEmpty,
  ExpandMore,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';

import { StepResult } from '../models';

const prettify = (text: string): string => {
  if (!text) return '(sin cuerpo)';
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
};

const StateIcon = ({ state }: { state: StepResult['state'] }) => {
  if (state === 'running') return <CircularProgress size={18} />;
  if (state === 'ok') return <CheckCircleOutline color='success' />;
  if (state === 'failed') return <ErrorOutline color='error' />;
  return <HourglassEmpty color='disabled' />;
};

const Block = ({ title, text }: { title: string; text: string }) => (
  <Box>
    <Typography variant='caption' color='text.secondary'>
      {title}
    </Typography>
    <Box
      component='pre'
      sx={{
        m: 0,
        mt: 0.5,
        p: 1,
        borderRadius: 1,
        bgcolor: 'action.hover',
        fontSize: 12,
        maxHeight: 320,
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {text}
    </Box>
  </Box>
);

interface Props {
  results: StepResult[];
}

const ResultsPanel = ({ results }: Props) => {
  if (!results.length) {
    return (
      <Typography variant='body2' color='text.secondary'>
        Todavía no corriste la secuencia.
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {results.map((result, index) => (
        <Accordion key={`${result.stepId}-${index}`} disableGutters>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack
              direction='row'
              spacing={1.5}
              alignItems='center'
              sx={{ width: '100%', pr: 1 }}
            >
              <StateIcon state={result.state} />
              <Typography sx={{ flexGrow: 1 }} noWrap>
                {index + 1}. {result.name}
              </Typography>
              {result.status !== null && (
                <Chip
                  size='small'
                  label={result.status}
                  color={result.state === 'ok' ? 'success' : 'error'}
                  variant='outlined'
                />
              )}
              {result.ms > 0 && (
                <Typography variant='caption' color='text.secondary'>
                  {result.ms} ms
                </Typography>
              )}
            </Stack>
          </AccordionSummary>

          <AccordionDetails>
            <Stack spacing={1.5}>
              {result.error && <Alert severity='error'>{result.error}</Alert>}

              <Typography variant='caption' color='text.secondary'>
                {result.method} {result.url}
              </Typography>

              {Object.keys(result.captured).length > 0 && (
                <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                  {Object.entries(result.captured).map(([name, value]) => (
                    <Chip
                      key={name}
                      size='small'
                      label={`${name} = ${value.slice(0, 60)}`}
                    />
                  ))}
                </Stack>
              )}

              {result.requestBody && (
                <Block title='Se envió' text={result.requestBody} />
              )}
              <Block title='Respondió' text={prettify(result.response)} />
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );
};

export default ResultsPanel;
